import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { isNative, nativeShare, openExternal } from '../native';
import {
  Share2,
  Twitter,
  Facebook,
  Mail,
  Copy,
  Check,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShareButton = ({ tournamentId, tournamentName, variant = "outline", size = "sm" }) => {
  const [loading, setLoading] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyText, setCopyText] = useState('');
  const [copied, setCopied] = useState(false);

  const handleNativeShare = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/share/tournament`, {
        tournament_id: tournamentId,
        platform: 'native'
      });
      await nativeShare({
        title: tournamentName,
        text: response.data.copy_text,
        url: response.data.share_url,
      });
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (platform) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/share/tournament`, {
        tournament_id: tournamentId,
        platform
      });

      if (platform === 'copy') {
        setCopyText(response.data.copy_text);
        setShowCopyDialog(true);
      } else if (response.data.share_url) {
        openExternal(response.data.share_url);
      }

      toast.success(`Shared to ${platform}!`);
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => {
        setCopied(false);
        setShowCopyDialog(false);
      }, 1500);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  if (isNative()) {
    // On native, hand off straight to the OS share sheet -- it already
    // offers every destination (Messages, Mail, social apps, copy, etc.)
    // without us reimplementing a platform picker.
    return (
      <Button
        variant={variant}
        size={size}
        className="gap-2"
        onClick={handleNativeShare}
        disabled={loading}
        data-testid="share-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
        Share
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className="gap-2" data-testid="share-button">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem 
            onClick={() => handleShare('twitter')}
            className="gap-2 cursor-pointer"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4 text-[#1DA1F2]" />
            Twitter / X
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleShare('facebook')}
            className="gap-2 cursor-pointer"
            data-testid="share-facebook"
          >
            <Facebook className="w-4 h-4 text-[#4267B2]" />
            Facebook
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleShare('email')}
            className="gap-2 cursor-pointer"
            data-testid="share-email"
          >
            <Mail className="w-4 h-4 text-muted-foreground" />
            Email
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleShare('copy')}
            className="gap-2 cursor-pointer"
            data-testid="share-copy"
          >
            <Copy className="w-4 h-4 text-muted-foreground" />
            Copy Link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Copy Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Tournament</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap">
              {copyText}
            </div>
            <Button 
              onClick={handleCopy} 
              className="w-full btn-primary gap-2"
              data-testid="copy-text-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Favorite Button Component
export const FavoriteButton = ({ tournamentId, initialFavorited = false, onToggle }) => {
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [loading, setLoading] = useState(false);

  const toggleFavorite = async () => {
    setLoading(true);
    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${tournamentId}`);
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { tournament_id: tournamentId });
        setIsFavorited(true);
        toast.success('Added to favorites!');
      }
      if (onToggle) onToggle(!isFavorited);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to save favorites');
      } else {
        toast.error('Failed to update favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleFavorite}
      disabled={loading}
      className={`gap-2 ${isFavorited ? 'bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20' : ''}`}
      data-testid="favorite-button"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <svg 
          className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
          />
        </svg>
      )}
      {isFavorited ? 'Saved' : 'Save'}
    </Button>
  );
};
